'use client';
import ReserveSeeNotify from '@/components/shared/ReserveSeeNotify/ReserveSeeNotify';
import Conditional from '@/components/shared/conditional/conditional.component';
import Typography from '@/components/shared/typography/typography.component';
import React, { useEffect, useState } from 'react';
import { useTranslation, useWindowDimensions } from '@/hook';
import {
    HighlightsIcon,
    Speedometer2,
    TransmissionIcon2,
    CalendarBookedCar2,
    carFrame,
    engine,
    Speedometer,
    CalendarCardIcon,
    TransmissionIcon,
} from '../../../../../public/Images';
import BadgeWithTooltip, {
    EBadgeWithTooltip,
} from '@/components/shared/badege-with-tooltip/badge-with-tooltip.component';
import WhatsIncluded from '@/components/shared/WhatsIncluded/whats-included.component';
import Modal from '@/components/shared/modal.component';
import { DisplayedCarInfoInterface, highlightsType } from '@/types';
import { formatNumber, getCarCardLocale, numberWithCommas } from '@/utils';
import NotifyMe from '../NotifyMe';
import {
    CarProfileCarName,
    CarProfileCurrency,
    CarProfileInfoContainer,
    CarProfileInstallmentPrice,
    CarProFileMoreInfoContainer,
    CarProFileMoreInfoItemContainer,
    CarProfileMoreInfoText,
    CarProfileSubtitle,
    CarProfileTrim,
} from '../StyledComponents/car-info-section-components';
import CarSoldOverlay from '../SimilarCars/CarSoldOverlay';
import SoldBadge from '@/components/shared/SoldBadge/sold-badge.component';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import ShowroomSection from './ShowroomSection/ShowroomSection';
import colors from '@/theme/colors.module.scss';
import styles from './styles.module.scss';
import {
    AVAILABLE_LANGUAGES,
    FEATURE_FLAG_SHOW_NEW_CAR_SUMMARY,
} from '@/constants';
import { FlapperLine } from '@/components/shared/car-details-card/car-details-car-client.component';
import { getCarListingFebDiscounts } from '@/api-requests/feb-discounts.apis';

const MorInfo = ({
    inspectionRef,
    highlights,
    isRepainted,
    numberOfRepaintedPanels,
    kilometers,
    transmission,
    year,
}: {
    inspectionRef: React.RefObject<HTMLDivElement>;
    highlights: highlightsType;
    isRepainted: boolean;
    numberOfRepaintedPanels: number;
    kilometers: string | number;
    year: number;
    transmission: string;
}) => {
    const { t } = useTranslation();
    return (
        <div className="border border-solid border-gray-100 p-4 pt-5 mb-4 rounded-lg md:border-0 md:p-0 md:mb-0">
            <div className="mb-5 md:mb-0">
                <CarProFileMoreInfoContainer>
                    <CarProFileMoreInfoItemContainer>
                        <Image
                            src={Speedometer2}
                            alt="Speedometer"
                            className="h-5 w-5"
                        />
                        <CarProfileMoreInfoText
                            variant={'body2'}
                        >{`${numberWithCommas(kilometers)} ${t(
                            'km'
                        )}`}</CarProfileMoreInfoText>
                    </CarProFileMoreInfoItemContainer>
                    <CarProFileMoreInfoItemContainer>
                        <Image
                            src={CalendarBookedCar2}
                            alt="CalendarCardIcon"
                            className="h-5 w-5"
                        />
                        <CarProfileMoreInfoText variant={'body2'}>
                            {year}
                        </CarProfileMoreInfoText>
                    </CarProFileMoreInfoItemContainer>
                    <CarProFileMoreInfoItemContainer>
                        <Image
                            src={TransmissionIcon2}
                            alt="TransmissionIcon"
                            className="h-5 w-5"
                        />
                        {/*
          @eslint-disable-next-line @typescript-eslint/ban-ts-comment
          @ts-ignore */}
                        <CarProfileMoreInfoText variant={'body2'}>
                            {t(transmission)}
                        </CarProfileMoreInfoText>
                    </CarProFileMoreInfoItemContainer>
                </CarProFileMoreInfoContainer>
            </div>
            <div className="w-full h-[1px] bg-gray-100 mb-5 md:hidden"></div>
            <div className="md:hidden flex flex-col gap-y-4">
                <div className="flex gap-x-2 items-center">
                    <Image className="w-5 h-5" src={carFrame} alt="frame" />
                    {isRepainted ? (
                        numberOfRepaintedPanels > 3 ? (
                            <div className="text-gray-600 text-sm font-normal leading-tight">
                                {t('fewRepaintedParts')}
                            </div>
                        ) : numberOfRepaintedPanels === 1 ? (
                            <div className="text-gray-600 text-sm font-normal leading-tight">
                                {numberOfRepaintedPanels} {t('repaintedPart')}
                            </div>
                        ) : (
                            <div className="text-gray-600 text-sm font-normal leading-tight">
                                {numberOfRepaintedPanels} {t('repaintedParts')}
                            </div>
                        )
                    ) : (
                        <div className="text-gray-600 text-sm font-normal leading-tight">
                            {t('noRepaintedParts')}
                        </div>
                    )}
                </div>
                <div className="flex gap-x-2 items-center">
                    <Image className="w-5 h-5" src={engine} alt="engine" />

                    <div className="text-gray-600 text-sm font-normal leading-tight">
                        {t('noMechProblems')}
                    </div>
                </div>
                {highlights?.ar?.map((h, index) => (
                    <div
                        className="flex gap-x-2 items-center"
                        key={`${h}-highlight`}
                    >
                        <Image
                            className="w-5 h-5"
                            src={HighlightsIcon}
                            alt="star"
                        />
                        <div className="en:hidden text-gray-600 text-sm font-normal leading-tight">
                            {highlights?.ar[index]}
                        </div>
                        <div className="ar:hidden text-gray-600 text-sm font-normal leading-tight">
                            {highlights?.en[index]}
                        </div>
                    </div>
                ))}
                <button
                    onClick={() => {
                        if (inspectionRef?.current) {
                            const targetPosition =
                                inspectionRef?.current?.getBoundingClientRect()
                                    .top + window.scrollY;
                            window.scrollTo({
                                top: targetPosition,
                                behavior: 'smooth',
                            });
                        }
                    }}
                    className="border border-solid border-gray-300 rounded-lg py-2.5 px-4 justify-content-center items-center text-sm text-gray-700 font-medium leading-tight"
                >
                    {t('Inspection Report')}
                </button>
            </div>
        </div>
    );
};
const PricingSection = ({
    sylndrSellingPrice,
    installmentPrice,
    discountPrice,
    febDiscount,
    registrationFees,
}: {
    sylndrSellingPrice: string | number;
    installmentPrice: string | number;
    discountPrice: number;
    febDiscount: number;
    registrationFees?: number;
}) => {
    const { t, i18n } = useTranslation();
    const { IS_DESKTOP } = useWindowDimensions();
    const carPriceSubSection = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
    };
    const carPriceSubSectionText = {
        display: 'flex',
        alignItems: 'baseline',
    };
    const hasDiscount = discountPrice !== undefined && discountPrice !== 0;

    const differencs = Math.trunc(
        parseFloat(sylndrSellingPrice as string) - discountPrice
    );

    const discountPercentage = formatNumber(differencs);
    const displayedPrice = hasDiscount
        ? discountPrice.toString()
        : sylndrSellingPrice;
    return (
        <div className={styles.priceSectionContainer}>
            {IS_DESKTOP && (
                <CarProfileSubtitle variant={'body2Medium'}>
                    {t('pricing')}
                </CarProfileSubtitle>
            )}
            <Conditional condition={hasDiscount}>
                <div className={styles.flexCenter}>
                    <div className={styles.displayFlex}>
                        <Typography
                            variant="caption"
                            className={`${styles.discountPrice} ${
                                i18n.language === 'ar'
                                    ? 'strikethroughAr'
                                    : 'strikethrough'
                            }`}
                        >
                            {`${numberWithCommas(sylndrSellingPrice)}`}
                        </Typography>
                        <Typography
                            className={styles.discountCurrency}
                            variant="caption"
                        >
                            {t('EGP')}
                        </Typography>
                    </div>
                    <div className={styles.discountPercentageContainer}>
                        <Typography
                            className={styles.discountPercentageValue}
                            variant="subtitle1SemiBold"
                        >
                            {`${discountPercentage}`}
                        </Typography>
                    </div>
                </div>
            </Conditional>
            <div style={carPriceSubSection}>
                <div style={carPriceSubSectionText}>
                    <CarProfileCurrency variant={'body2'}>
                        {t('EGP')}
                    </CarProfileCurrency>
                    <Typography
                        variant={'h6SemiBold'}
                        color={colors.secondary500}
                    >
                        {numberWithCommas(displayedPrice as string)}
                    </Typography>
                </div>
                {installmentPrice && (
                    <CarProfileInstallmentPrice variant={'body2Medium'}>
                        {t('from')} {numberWithCommas(installmentPrice)}
                        {t('/month')}
                    </CarProfileInstallmentPrice>
                )}
            </div>
            <div>
                <FlapperLine
                    extraClasses={' rounded-md '}
                    hasDiscount={hasDiscount}
                    discountText={t('limitedOffer')}
                    febDiscount={febDiscount}
                    febDiscountText={t('FEB_DISCOUNT')}
                />
            </div>
            <div className={styles.whatsIncluded}>
                <WhatsIncluded
                    hideToolTip={!IS_DESKTOP}
                    registrationFees={registrationFees}
                />
            </div>
        </div>
    );
};
const MorInfoWithouSummary = ({
    kilometers,
    transmission,
    year,
}: {
    kilometers: string | number;
    year: number;
    transmission: string;
}) => {
    const { t } = useTranslation();
    return (
        <div className="py-3 md:py-0 border-t border-t-gray-200 md:border-none">
            <CarProFileMoreInfoContainer>
                <CarProFileMoreInfoItemContainer>
                    <Image src={Speedometer} alt="Speedometer" />
                    <CarProfileMoreInfoText
                        variant={'body2'}
                    >{`${numberWithCommas(kilometers)} ${t(
                        'km'
                    )}`}</CarProfileMoreInfoText>
                </CarProFileMoreInfoItemContainer>
                <CarProFileMoreInfoItemContainer>
                    <Image src={CalendarCardIcon} alt="CalendarCardIcon" />
                    <CarProfileMoreInfoText variant={'body2'}>
                        {year}
                    </CarProfileMoreInfoText>
                </CarProFileMoreInfoItemContainer>
                <CarProFileMoreInfoItemContainer>
                    <Image src={TransmissionIcon} alt="TransmissionIcon" />
                    {/*
          @eslint-disable-next-line @typescript-eslint/ban-ts-comment
          @ts-ignore */}
                    <CarProfileMoreInfoText variant={'body2'}>
                        {t(transmission)}
                    </CarProfileMoreInfoText>
                </CarProFileMoreInfoItemContainer>
            </CarProFileMoreInfoContainer>
        </div>
    );
};

const CarInfo = ({
    carInfo,
    highlights,
    isRepainted,
    numberOfRepaintedPanels,
    inspectionRef,
    imperfectionReportExpirementIsActive,
    isExpirementLoading,
}: {
    carInfo: DisplayedCarInfoInterface;
    highlights: highlightsType;
    isRepainted: boolean;
    imperfectionReportExpirementIsActive: boolean;
    isExpirementLoading: boolean;
    numberOfRepaintedPanels: number;
    inspectionRef: React.RefObject<HTMLDivElement>;
}) => {
    const { i18n } = useTranslation();
    const { width } = useWindowDimensions();
    const lang = i18n.language;
    const IS_RTL = lang === 'ar';
    const isDesktop = width > 850;
    const [notifyMeModal, setNotifyModal] = useState(false);
    const router = useRouter();
    const [febDiscount, setFebDiscount] = useState(0);
    useEffect(() => {
        getCarListingFebDiscounts().then(discounts => {
            if (discounts[carInfo.id]) {
                setFebDiscount(discounts[carInfo.id]);
            }
        });
    }, [setFebDiscount]);

    const seeCarButtonClicked = () => {
        if (carInfo?.id === undefined) return;
        router.push(
            `/${i18n.language}/car-details/${carInfo?.id}/checkout/see`
        );
    };
    const handleBadge = (): React.JSX.Element => {
        if (carInfo.isSold) return <SoldBadge />;

        if (carInfo.isPurchaseInProgress || carInfo.isReserved) {
            const translations = getCarCardLocale();
            return (
                <BadgeWithTooltip
                    translations={translations}
                    type={
                        carInfo.isPurchaseInProgress
                            ? EBadgeWithTooltip.PURCHASE_IN_PROGRESS
                            : EBadgeWithTooltip.RESERVED
                    }
                />
            );
        }
        return <></>;
    };

    return (
        <>
            <CarProfileInfoContainer>
                <div>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            position: 'relative',
                            flexDirection: 'column',
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                position: 'relative',
                                flexDirection: 'row',
                                width: '100%',
                            }}
                        >
                            <div>
                                <CarProfileCarName variant="subtitle2SemiBold">{`${carInfo?.make} ${carInfo?.model} ${carInfo?.year}`}</CarProfileCarName>
                                {carInfo?.trim && (
                                    <CarProfileTrim variant={'body2'}>
                                        {IS_RTL
                                            ? carInfo?.trim?.ar
                                            : carInfo?.trim?.en}
                                    </CarProfileTrim>
                                )}
                            </div>
                            {handleBadge()}
                        </div>

                        {(isDesktop ||
                            ((carInfo.isSold || carInfo.isPurchaseInProgress) &&
                                !isDesktop)) &&
                            (FEATURE_FLAG_SHOW_NEW_CAR_SUMMARY ? (
                                !isExpirementLoading ? (
                                    imperfectionReportExpirementIsActive ? (
                                        <MorInfo
                                            inspectionRef={inspectionRef}
                                            highlights={highlights}
                                            isRepainted={isRepainted}
                                            numberOfRepaintedPanels={
                                                numberOfRepaintedPanels
                                            }
                                            kilometers={carInfo?.kilometers}
                                            year={carInfo?.year}
                                            transmission={carInfo?.transmission}
                                        />
                                    ) : (
                                        <MorInfoWithouSummary
                                            kilometers={carInfo?.kilometers}
                                            year={carInfo?.year}
                                            transmission={carInfo?.transmission}
                                        />
                                    )
                                ) : (
                                    <></>
                                )
                            ) : (
                                <MorInfoWithouSummary
                                    kilometers={carInfo?.kilometers}
                                    year={carInfo?.year}
                                    transmission={carInfo?.transmission}
                                />
                            ))}
                    </div>
                </div>
                {carInfo.showroom &&
                !carInfo.isPurchaseInProgress &&
                !carInfo.isSold &&
                isDesktop ? (
                    <ShowroomSection
                        showroom={carInfo.showroom}
                        lang={lang as AVAILABLE_LANGUAGES}
                        isDesktop={isDesktop}
                        carInfo={carInfo}
                        setNotifyModal={setNotifyModal}
                        seeCarButtonClicked={seeCarButtonClicked}
                    />
                ) : null}
                {(isDesktop ||
                    !(carInfo?.isSold || carInfo.isPurchaseInProgress)) && (
                    <div className={styles.pricingContainer}>
                        {(carInfo?.isSold || carInfo.isPurchaseInProgress) &&
                            isDesktop && (
                                <CarSoldOverlay
                                    type={
                                        carInfo.isPurchaseInProgress
                                            ? 'PURCHASE_IN_PROGRESS'
                                            : 'SOLD'
                                    }
                                    style={{
                                        marginLeft: '-16px',
                                        marginRight: '-16px',
                                        marginBottom: '-24px',
                                        marginTop: '-16px',
                                    }}
                                />
                            )}
                        <PricingSection
                            febDiscount={febDiscount}
                            sylndrSellingPrice={carInfo?.sylndrSellingPrice}
                            installmentPrice={carInfo?.installmentPrice}
                            discountPrice={carInfo?.discountPrice}
                            registrationFees={carInfo?.registrationFees || 0}
                        />
                        {!isDesktop &&
                            (FEATURE_FLAG_SHOW_NEW_CAR_SUMMARY ? (
                                !isExpirementLoading ? (
                                    imperfectionReportExpirementIsActive ? (
                                        <MorInfo
                                            inspectionRef={inspectionRef}
                                            highlights={highlights}
                                            isRepainted={isRepainted}
                                            numberOfRepaintedPanels={
                                                numberOfRepaintedPanels
                                            }
                                            kilometers={carInfo?.kilometers}
                                            year={carInfo?.year}
                                            transmission={carInfo?.transmission}
                                        />
                                    ) : (
                                        <MorInfoWithouSummary
                                            kilometers={carInfo?.kilometers}
                                            year={carInfo?.year}
                                            transmission={carInfo?.transmission}
                                        />
                                    )
                                ) : (
                                    <></>
                                )
                            ) : (
                                <MorInfoWithouSummary
                                    kilometers={carInfo?.kilometers}
                                    year={carInfo?.year}
                                    transmission={carInfo?.transmission}
                                />
                            ))}

                        {carInfo.showroom &&
                        !carInfo.isPurchaseInProgress &&
                        !carInfo.isSold &&
                        !isDesktop ? (
                            <ShowroomSection
                                showroom={carInfo.showroom}
                                lang={lang as AVAILABLE_LANGUAGES}
                                isDesktop={isDesktop}
                                carInfo={carInfo}
                                setNotifyModal={setNotifyModal}
                                seeCarButtonClicked={seeCarButtonClicked}
                            />
                        ) : null}
                        {isDesktop ? (
                            <ReserveSeeNotify
                                carInfo={carInfo}
                                lang={lang}
                                setNotifyModal={setNotifyModal}
                                seeCarButtonClicked={seeCarButtonClicked}
                                showTip={true}
                            />
                        ) : null}
                        <Modal
                            open={notifyMeModal}
                            sx={{
                                backdropFilter: 'blur(6px)',
                                borderRadius: '12px',
                            }}
                            setOpen={setNotifyModal}
                        >
                            <NotifyMe
                                setModal={setNotifyModal}
                                carId={carInfo?.id}
                            />
                        </Modal>
                    </div>
                )}
            </CarProfileInfoContainer>
        </>
    );
};
export default CarInfo;
